import { useState } from "react";
import {
  Avatar,
  Combobox,
  Group,
  Input,
  InputBase,
  Text,
  useCombobox,
} from "@mantine/core";
import { useUserDetailsStore } from "shared/stores/userDetailsStore";
import { Account } from "shared/types/UserDetailsTypes";
import { useTranslation } from "react-i18next";
import { useDeviceStore } from "shared/stores/deviceStore";
import { colorByAccountType, createAccountName } from "shared/utils";
import { PasswordManagerFormData } from "widgets/passwordmanager/types";

type SelectOptionProps = Account & { selectedAccount?: boolean };

function SelectOption({
  selectedAccount = false,
  ...account
}: SelectOptionProps) {
  return (
    <Group
      style={{ height: "100%", paddingLeft: selectedAccount ? "10px" : "5px" }}
    >
      {!selectedAccount && (
        <Avatar
          bg={colorByAccountType(account.License, account.IsDemo)}
          color="transparent"
          size={50}
        />
      )}

      <div>
        <Text fz="h3" fw={500}>
          {createAccountName(account)}
        </Text>
        <Text fz="xl" opacity={0.6}>
          TP: {account.Name}
        </Text>
      </div>
    </Group>
  );
}

export function AccountsSelect({
  onOptionSelect,
  formData,
}: {
  onOptionSelect: (valueObject: { name: string; value: string }) => void;
  formData: PasswordManagerFormData;
}) {
  const isMobile = useDeviceStore((state) => state.isMobile);

  const { t } = useTranslation();
  const userDetails = useUserDetailsStore((state) => state.userDetails);

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
  });

  const [value, setValue] = useState<string | null>(formData.account.value);
  const selectedOption = userDetails?.Accounts.find(
    (item) => item.Name === value
  );

  const options = userDetails?.Accounts.map((item, index) => (
    <Combobox.Option
      style={{
        height: "60px",
        borderBottom:
          userDetails?.Accounts.length - 1 !== index
            ? "1px solid #E6E8F0"
            : "none",
      }}
      value={JSON.stringify({
        name: createAccountName(item),
        value: item.Name,
      })}
      key={item.Name}
    >
      <SelectOption {...item} />
    </Combobox.Option>
  ));

  return (
    <Combobox
      transitionProps={{ duration: 200, transition: "pop" }}
      store={combobox}
      withinPortal={false}
      onOptionSubmit={(val) => {
        const { name, value } = JSON.parse(val);
        console.log(name, value);
        setValue(value);
        combobox.closeDropdown();
        onOptionSelect({ name: name, value: value });
      }}
    >
      <Combobox.Target>
        <InputBase
          styles={{
            input: {
              height: "60px",
              fontSize: "1.6rem",
              border: isMobile ? "none" : "1px solid #C3C6D4",
            },
          }}
          component="button"
          type="button"
          pointer
          rightSection={
            <img
              src={"/assets/svg/common/ArrowIcon.svg"}
              width={16}
              height={16}
              style={{ pointerEvents: "none" }}
              alt="arrow"
            />
          }
          rightSectionWidth={100}
          onClick={() => combobox.toggleDropdown()}
          rightSectionPointerEvents="none"
          multiline
        >
          {selectedOption ? (
            <SelectOption {...selectedOption} selectedAccount={true} />
          ) : (
            <Input.Placeholder
              style={{
                color: "var(--disabled-text-color)",
                fontWeight: "300",
                marginLeft: "10px",
              }}
            >
              {t("PasswordManager.Setup.SelectAccount")}
            </Input.Placeholder>
          )}
        </InputBase>
      </Combobox.Target>

      <Combobox.Dropdown>
        <Combobox.Options>{options}</Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  );
}
